package com.arena_web.ui.components.overlay

import com.arena_web.data.dto.SubscriptionConfirmRequest
import com.arena_web.data.repository.ApiCallException
import com.arena_web.domain.repositories.SubscriptionRepository
import com.arena_web.utils.MetaPixel
import com.arena_web.utils.Storage
import com.arena_web.utils.TikTokPixel
import com.arena_web.utils.StorageKeys
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.w3c.dom.PageTransitionEvent

// =============================================================================
// plata by mono (monobank acquiring) subscription checkout. There is nothing to
// embed: the backend creates a hosted-page invoice and we redirect the whole tab
// to it. mono sends the payer back to the app afterwards, where the pending order
// saved in localStorage is confirmed (pull) — the webhook activates the
// subscription server-side regardless, the pull only makes the state and the
// toast immediate.
// =============================================================================

/**
 * Starts the Access subscription checkout: creates the pending order + mono
 * invoice on the backend, remembers it in localStorage and redirects to the
 * payment page. On success the current page unloads — there is no "approved"
 * callback; the result is handled by [resumePendingSubscription] after the
 * redirect back. [onFailed] fires only when checkout itself fails.
 */
fun startSubscriptionCheckout(
    scope: CoroutineScope,
    repository: SubscriptionRepository,
    onFailed: (String?) -> Unit,
    source: String = "unknown",
    trial: Boolean = false,
) {
    scope.launch {
        val checkout =
            repository.checkout(source, trial).getOrElse { e ->
                onFailed(e.message)
                return@launch
            }
        val pageUrl = checkout.pageUrl
        if (pageUrl.isBlank()) {
            onFailed(null)
            return@launch
        }
        Storage.save(StorageKeys.SUBSCRIPTION_PENDING_ORDER, checkout.orderReference)
        Storage.remove(StorageKeys.SUBSCRIPTION_PENDING_ORDER_ATTEMPTS)
        // Mid-funnel conversion signal: the invoice was created and we are about
        // to redirect to the mono payment page. Value/currency come from the
        // server-priced invoice (fallbacks only if the response is malformed).
        val currency = checkout.currency.ifBlank { "USD" }
        if (trial) {
            val value = checkout.amount.toDoubleOrNull() ?: 0.99
            // Trial funnel: Meta has a standard StartTrial event; TikTok has no
            // trial event, so the trial-priced InitiateCheckout stands in.
            MetaPixel.trackStartTrial(value = value, currency = currency)
            TikTokPixel.trackInitiateCheckout(value = value, currency = currency)
        } else {
            val value = checkout.amount.toDoubleOrNull() ?: 14.99
            MetaPixel.trackInitiateCheckout(value = value, currency = currency)
            TikTokPixel.trackInitiateCheckout(value = value, currency = currency)
        }
        // If the payer comes back via the browser Back button, the page is
        // restored from the back/forward cache with all its JS state frozen
        // mid-checkout (busy CTA stuck on "processing", stale coroutines).
        // Force a fresh load in that case — app startup then resumes the
        // pending order and re-renders the real subscription state.
        window.addEventListener(
            "pageshow",
            { event ->
                if (event.unsafeCast<PageTransitionEvent>().persisted) {
                    window.location.reload()
                }
            },
        )
        window.location.href = pageUrl
    }
}

/**
 * Confirms the pending subscription order left in localStorage, if any. Called
 * once on app load — that covers both the redirect back from the mono payment
 * page and a tab the user closed mid-payment and reopened later.
 *
 * The order is pull-confirmed with a few retries while the invoice is still
 * settling. Returns true when the subscription came back active. A still-pending
 * order (user backed out) clears the marker silently — the webhook activates it
 * if it does get paid later, and the next subscription refresh reflects it.
 */
suspend fun resumePendingSubscription(repository: SubscriptionRepository): Boolean {
    val orderReference = Storage.get(StorageKeys.SUBSCRIPTION_PENDING_ORDER) ?: return false

    var active: Boolean? = null
    for (attempt in 0 until 3) {
        if (attempt > 0) delay(1500)
        val result = repository.confirmCheckout(SubscriptionConfirmRequest(orderReference))
        val confirmed = result.getOrNull()
        if (confirmed != null) {
            active = confirmed.active
            if (confirmed.active) break
            continue
        }
        // Terminal: the backend will never accept this order (unknown order 404,
        // someone else's order 403, malformed 400/422, gone 410). Drop the marker
        // so we don't retry it on every page load forever.
        val status = (result.exceptionOrNull() as? ApiCallException)?.statusCode
        if (status != null && status in TERMINAL_CONFIRM_STATUSES) {
            clearPendingOrder()
            return false
        }
    }

    if (active == null) {
        // Transient (network / 5xx / expired session): keep the marker for the
        // next load, but only for a bounded number of loads — the webhook
        // activates a paid order server-side regardless.
        val loads = Storage.getInt(StorageKeys.SUBSCRIPTION_PENDING_ORDER_ATTEMPTS) + 1
        if (loads >= MAX_PENDING_ORDER_LOADS) {
            clearPendingOrder()
        } else {
            Storage.saveInt(StorageKeys.SUBSCRIPTION_PENDING_ORDER_ATTEMPTS, loads)
        }
        return false
    }

    clearPendingOrder()
    // NB: the Purchase/Subscribe conversion is now fired SERVER-SIDE from the
    // mono status-apply path (MetaCapiService.sendPurchase), so it survives
    // adblock/ITP and matches on the fbp/fbclid stored at signup. Firing it here
    // too would double-count (no shared event_id), so the client no longer does.
    return active
}

private val TERMINAL_CONFIRM_STATUSES = setOf(400, 403, 404, 410, 422)
private const val MAX_PENDING_ORDER_LOADS = 5

private fun clearPendingOrder() {
    Storage.remove(StorageKeys.SUBSCRIPTION_PENDING_ORDER)
    Storage.remove(StorageKeys.SUBSCRIPTION_PENDING_ORDER_ATTEMPTS)
}

/**
 * True when a repo error came from the backend rejecting a premium action for
 * lack of an active subscription. The HTTP layer collapses the status into the
 * exception message, so we match the 402 code / subscription/payment wording.
 * Used as a safety net alongside the proactive `isSubscribed` gate.
 */
fun isPaymentRequiredError(message: String?): Boolean {
    if (message == null) return false
    val m = message.lowercase()
    return m.contains("402") ||
        m.contains("payment required") ||
        m.contains("subscription") ||
        m.contains("insufficient")
}
